import React, { useEffect, useRef, useState } from 'react';
import { api } from '../../../api.js';
import { useI18n } from '../../../i18n/index.jsx';

// Crop an image before upload: drag to position, slider to zoom, inside a
// frame of the target shape. The result is drawn on a canvas at the target
// width and compressed to WebP (JPEG where the browser can't), then
// uploaded to the media library. Works with a picked file or an image
// already on the site (same origin), e.g. to re-crop.
// returnBlob: give the cropped image back ({ blob }) instead of uploading
// it (product photos, uploaded with the product). aspects: shapes the user
// can switch between ([{ value, label }]).
const toBlob = (canvas, type) => new Promise((resolve) => canvas.toBlob(resolve, type, 0.82));

export default function CropModal({ source, aspect: initialAspect = 1, aspects, outWidth = 1200, returnBlob = false, onDone }) {
  const { t, errorText } = useI18n();
  const [aspect, setAspect] = useState(initialAspect);
  const [img, setImg] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const drag = useRef(null);
  const frameW = Math.min(520, window.innerWidth - 80);
  const frameH = Math.round(frameW / aspect);

  useEffect(() => {
    const url = typeof source === 'string' ? source : URL.createObjectURL(source);
    const i = new Image();
    i.onload = () => setImg(i);
    i.onerror = () => setError(t('errors.invalid_media'));
    i.src = url;
    return () => { if (typeof source !== 'string') URL.revokeObjectURL(url); };
  }, [source, t]);

  const base = img ? Math.max(frameW / img.naturalWidth, frameH / img.naturalHeight) : 1;
  const scale = base * zoom;
  const w = img ? img.naturalWidth * scale : 0;
  const h = img ? img.naturalHeight * scale : 0;
  const clamp = (p) => ({ x: Math.min(0, Math.max(frameW - w, p.x)), y: Math.min(0, Math.max(frameH - h, p.y)) });
  // Centre once loaded; keep the image covering the frame when zooming.
  useEffect(() => { if (img) setPos({ x: (frameW - w) / 2, y: (frameH - h) / 2 }); }, [img, aspect]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setPos((p) => clamp(p)); }, [zoom]); // eslint-disable-line react-hooks/exhaustive-deps

  const down = (e) => { e.preventDefault(); drag.current = { x: e.clientX, y: e.clientY, start: pos }; e.currentTarget.setPointerCapture?.(e.pointerId); };
  const move = (e) => { if (drag.current) setPos(clamp({ x: drag.current.start.x + e.clientX - drag.current.x, y: drag.current.start.y + e.clientY - drag.current.y })); };
  const up = () => { drag.current = null; };
  const key = (e) => {
    const step = e.shiftKey ? 20 : 5;
    const d = { ArrowLeft: [step, 0], ArrowRight: [-step, 0], ArrowUp: [0, step], ArrowDown: [0, -step] }[e.key];
    if (d) { e.preventDefault(); setPos((p) => clamp({ x: p.x + d[0], y: p.y + d[1] })); }
  };

  async function save() {
    setBusy(true); setError('');
    try {
      const sx = -pos.x / scale, sy = -pos.y / scale, sw = frameW / scale, sh = frameH / scale;
      const outW = Math.round(Math.max(320, Math.min(outWidth, sw * 1.0 || outWidth)));
      const outH = Math.round(outW / aspect);
      const c = document.createElement('canvas');
      c.width = outW; c.height = outH;
      const ctx = c.getContext('2d');
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, outW, outH);
      let blob = await toBlob(c, 'image/webp');
      if (!blob || blob.type !== 'image/webp') blob = await toBlob(c, 'image/jpeg');
      if (returnBlob) { onDone({ blob }); return; }
      const fd = new FormData();
      fd.append('width', String(outW)); fd.append('height', String(outH));
      const base = typeof source === 'string' ? source.split('/').pop().replace(/\.[^.]+$/, '') : source.name.replace(/\.[^.]+$/, '');
      fd.append('file', blob, `${base}-crop.${blob.type === 'image/webp' ? 'webp' : 'jpg'}`);
      onDone(await api('/admin/content/media', { method: 'POST', form: fd }));
    } catch (e) { setError(errorText(e)); setBusy(false); }
  }

  return (
    <div className="modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onDone(null); }}>
      <div className="modal crop-modal" role="dialog" aria-modal="true" aria-labelledby="crop-title">
        <div className="modal-head">
          <h2 id="crop-title">{t('admin.content.crop.title')}</h2>
          <button type="button" className="btn btn-link" onClick={() => onDone(null)} disabled={busy} aria-label={t('common.close')}>✕</button>
        </div>
        <div className="modal-body crop-body">
          <p className="muted small">{t('admin.content.crop.help')}</p>
          {aspects?.length > 1 && (
            <div className="chip-choice" role="radiogroup" aria-label={t('admin.content.crop.shape')}>
              {aspects.map((a) => (
                <button key={a.label} type="button" role="radio" aria-checked={aspect === a.value} className={`chip-toggle${aspect === a.value ? ' on' : ''}`} onClick={() => setAspect(a.value)}>{a.label}</button>
              ))}
            </div>
          )}
          <div className="crop-frame" style={{ width: frameW, height: frameH }} tabIndex={0} onKeyDown={key}
            onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} aria-label={t('admin.content.crop.frame')}>
            {img && <img src={img.src} alt="" draggable="false" style={{ width: w, height: h, transform: `translate(${pos.x}px, ${pos.y}px)` }} />}
          </div>
          <label className="crop-zoom">
            <span>{t('admin.content.crop.zoom')}</span>
            <input type="range" min="1" max="3" step="0.01" value={zoom} onChange={(e) => setZoom(Number(e.target.value))} disabled={!img} />
          </label>
          {error && <p className="field-error">{error}</p>}
          <div className="editor-actions">
            <button type="button" className="btn btn-ghost" onClick={() => onDone(null)} disabled={busy}>{t('common.cancel')}</button>
            <button type="button" className="btn btn-primary" onClick={save} disabled={!img || busy}>{busy ? (returnBlob ? t('common.saving') : t('admin.content.media.uploading')) : t('admin.content.crop.save')}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
